<template lang="pug">
el-dialog(
  title="Importar parceiros",
  :visible="visible",
  @update:visible="emit('update:visible', $event)"
)
  div(v-if="visible")
    p Importe uma lista de parceiros a partir de um arquivo CSV:

    input(type="file", accept="text/csv", @change="handleFileUpload")

  template(slot="footer", style="text-align: right")
    el-button(@click="downloadTemplate") Baixar template
    el-button(type="primary", @click="handleImport", :loading="loading") Importar
</template>
<script setup>
import { default as InfluencerService, types } from '@/services/influencer/InfluencerService'
import { useMessage } from '@/utils/Message'
import { useAlert } from '@/utils/MessageBox'

import { ref } from 'vue'

const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  },
})

const emit = defineEmits(['update:visible', 'imported'])

const alert = useAlert()
const message = useMessage()

const file = ref(null)
const loading = ref(false)

function handleFileUpload(e) {
  file.value = e.target.files[0]
}

function downloadTemplate() {
  const link = document.createElement('a')
  link.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent('E-mail,Cupom,Tipo,Ativo,Afiliado\nexamplo@exemplo.com,COUPON1,colaborador,TRUE,FALSE')
  link.download = 'importar-parceiros-template.csv'
  link.click()
}

function handleImport() {
  if (file.value) {
    loading.value = true
    const reader = new FileReader()
    reader.onabort = () => {
      alert('Importação cancelada', 'A importação foi cancelada pelo usuário')
      loading.value = false
    }

    reader.onerror = () => {
      alert('Erro ao importar parceiros', 'Erro ao ler o arquivo')
      loading.value = false
    }

    reader.onload = async (e) => {
      try {
        const text = e.target.result.replace(/\r/g, '')

        // Parse CSV text into an array of objects
        // The CSV should contain a header with the columns 'E-mail', 'Cupom', 'Tipo', 'Ativo' and 'Afiliado'
        // This columns should be mapped to the 'email', 'coupon', 'type', 'active' and 'affiliate' properties of the object
        // Values should be trimmed and quotes should be removed
        const headers = text.split('\n')[0].split(',')
        const influencers = text.split('\n').slice(1).map((row, index) => {
          const values = row.split(',')
          const influencer = {
            email: values[headers.indexOf('E-mail')]?.trim()?.replace(/^"|"$/g, ''),
            coupon: values[headers.indexOf('Cupom')]?.trim()?.replace(/^"|"$/g, ''),
            type: values[headers.indexOf('Tipo')]?.trim()?.replace(/^"|"$/g, '')?.toLowerCase(),
            active: values[headers.indexOf('Ativo')]?.trim()?.replace(/^"|"$/g, '')?.toLowerCase(),
            affiliate: values[headers.indexOf('Afiliado')]?.trim()?.replace(/^"|"$/g, '')?.toLowerCase(),
          }

          // Verify that all fields are present
          if (!influencer.email || influencer.active === undefined || influencer.affiliate === undefined) {
            throw new Error(`Campos faltantes na linha ${index + 1}:  ${JSON.stringify(headers)}  ${JSON.stringify(values)} ${JSON.stringify(influencer)}`)
          }

          // Verify that type is one of types
          if (!(influencer.type in types)) {
            throw new Error(`Tipo inválido na linha ${index + 1}: ${influencer.type}`)
          }

          // Verify that active is true, false, sim or não
          if (influencer.active !== 'true' && influencer.active !== 'sim' && influencer.active !== 'false' && influencer.active !== 'não') {
            throw new Error(`Valor inválido para ativo na linha ${index + 1}: ${influencer.active}`)
          }

          // Verify that affiliate is true, false, sim or não
          if (influencer.affiliate !== 'true' && influencer.affiliate !== 'sim' && influencer.affiliate !== 'false' && influencer.affiliate !== 'não') {
            throw new Error(`Valor inválido para afiliado na linha ${index + 1}: ${influencer.affiliate}`)
          }

          influencer['promotedSkuId'] = 'br.com.magenta.basic'
          influencer['coupons'] = influencer.coupon ? [influencer.coupon] : []
          influencer['type'] = influencer.type ? influencer.type.toLowerCase() : null
          influencer['active'] = influencer.active === 'true' || influencer.active === 'sim'
          influencer['affiliate'] = influencer.affiliate === 'true' || influencer.affiliate === 'sim'
          delete influencer.coupon

          return influencer
        })

        if (influencers.length === 0) {
          alert('Nenhum parceiro foi encontrado no arquivo', 'Nenhum parceiro encontrado')
          return
        }

        const response = await InfluencerService.batchInsert({
          influencers
        })

        if (response.errors) {
          const errors = response.errors.join('\n- ')
          alert(`- ${errors}`, 'Falha ao importar parceiros')
          return
        }

        if (response.influencers.length == 1) {
          message.success(`1 parceiro importado com sucesso`)
        } else {
          message.success(`${response.influencers.length} parceiros importados com sucesso`)
        }
        emit('imported')
        emit('update:visible', false)
      } catch (e) {
        alert(e.message, 'Erro ao importar parceiros')
      } finally {
        loading.value = false
      }
    }

    reader.readAsText(file.value)
  }
}

</script>
